import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { ChangelogReleaseResponse } from '../lib/api-client';

export interface ChangelogFilters {
  tier?: string;
  stream?: string;
  category?: string;
}

export function useChangelog() {
  const apiClient = useApiClient();
  const [releases, setReleases] = useState<ChangelogReleaseResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFiltersState] = useState<ChangelogFilters>({});
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    setStatus('loading');
    const query: Record<string, string> = { limit: '25' };
    if (filters.tier) query.tier = filters.tier;
    if (filters.stream) query.stream = filters.stream;
    if (filters.category) query.category = filters.category;

    apiClient
      .listChangelog(query)
      .then((res) => {
        setReleases(res.data);
        setHasMore(res.has_more);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load changelog');
        setStatus('error');
      });
  }, [apiClient, fetchCount, filters]);

  const reload = useCallback(() => setFetchCount((n) => n + 1), []);

  const setFilters = useCallback((newFilters: ChangelogFilters) => {
    setFiltersState(newFilters);
    setReleases([]);
    setError(undefined);
  }, []);

  const loadingMoreRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    const last = releases[releases.length - 1];
    if (!last) return;
    loadingMoreRef.current = true;
    const query: Record<string, string> = { limit: '25', starting_after: last.id };
    if (filters.tier) query.tier = filters.tier;
    if (filters.stream) query.stream = filters.stream;
    if (filters.category) query.category = filters.category;

    try {
      const res = await apiClient.listChangelog(query);
      setReleases((prev) => [...prev, ...res.data]);
      setHasMore(res.has_more);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load more releases');
    } finally {
      loadingMoreRef.current = false;
    }
  }, [apiClient, releases, filters]);

  return { status, error, releases, hasMore, filters, setFilters, loadMore, reload };
}
